import React from 'react';
import { Box, Tooltip, Typography, Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { NOT_COMPUTED, tierLabel, tierMeta } from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';

/**
 * Shared shells for the benchmark panels.
 *
 * The card, the skeleton and the "no figure here" treatment were previously
 * copy-pasted into each of the six panels, which is how they drifted: two of
 * them rendered a permanent skeleton on failure, and each carried its own idea
 * of what an empty value looked like.
 */

/** A dashboard panel. */
export const SectionCard: React.FC<{
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    /** Colour for the icon badge. */
    accent?: string;
    /** Right-hand controls — filters, an export button. */
    actions?: React.ReactNode;
    /** Caveats on the whole panel — evidence strength, qualifications — beside the title. */
    badges?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, subtitle, icon, accent = '#FFDE59', actions, badges, children }) => (
    <Box className="rounded-2xl bg-surface/90 backdrop-blur-md border border-line p-6 shadow-2xl">
        <Box className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-line">
            <Box className="flex items-center gap-3">
                {icon && (
                    <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                            backgroundColor: `${accent}1A`,
                            border: `1px solid ${accent}4D`,
                            color: ink(accent)
                        }}
                    >
                        {icon}
                    </div>
                )}
                <Box>
                    <Box className="flex flex-wrap items-center gap-2">
                        <Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 700, color: dsVar('fg') }}>
                            {title}
                        </Typography>
                        {badges}
                    </Box>
                    {subtitle && (
                        <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.25 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
            {actions && <Box className="flex items-center gap-2">{actions}</Box>}
        </Box>
        <Box sx={{ mt: 2.5 }}>{children}</Box>
    </Box>
);

/**
 * Stands in wherever the run recorded no figure.
 *
 * Says so plainly rather than leaving a blank or, as before, substituting a
 * hardcoded number that looked exactly like a measured one.
 */
export const NotComputed: React.FC<{ detail?: string; compact?: boolean }> = ({ detail, compact }) => (
    <Box
        sx={{
            p: compact ? 1.5 : 3,
            borderRadius: '8px',
            border: `1px dashed ${dsVar('line')}`,
            backgroundColor: dsVar('page', 0.4),
            textAlign: 'center'
        }}
    >
        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>{NOT_COMPUTED}</Typography>
        {detail && (
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-faint'), mt: 0.5 }}>{detail}</Typography>
        )}
    </Box>
);

/**
 * The definition affordance next to a figure.
 *
 * Visible rather than hover-only on the label itself: the label already says
 * what the number means, and this is for the reader who wants the precise
 * definition and the technical term that goes with it.
 */
export const MetricInfo: React.FC<{ definition: string }> = ({ definition }) => (
    <Tooltip title={definition} arrow placement="top">
        <InfoOutlinedIcon
            sx={{ fontSize: 14, color: dsVar('fg-muted'), cursor: 'help', '&:hover': { color: dsVar('fg-2') } }}
        />
    </Tooltip>
);

/**
 * A panel that failed to load.
 *
 * Distinct from the skeleton on purpose. The old dashboard showed its loading
 * placeholder forever when a request failed, which reads as "still working"
 * and gives the reader nothing to act on.
 */
export const PanelError: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
    <Box
        sx={{
            p: 3,
            borderRadius: '8px',
            border: '1px solid rgba(248, 113, 113, 0.35)',
            backgroundColor: 'rgba(248, 113, 113, 0.08)'
        }}
    >
        <Typography sx={{ fontSize: '0.875rem', color: ink('#FCA5A5') }}>{message}</Typography>
        {onRetry && (
            <Button
                onClick={onRetry}
                startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{
                    mt: 1.5,
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: dsVar('accent-fg'),
                    '&:hover': { backgroundColor: 'rgba(255, 222, 89, 0.1)' }
                }}
            >
                Try again
            </Button>
        )}
    </Box>
);

/** Loading placeholder shaped like the panel it stands in for. */
export const PanelSkeleton: React.FC<{ height?: number }> = ({ height = 320 }) => (
    <Box
        className="rounded-2xl bg-surface border border-line animate-pulse"
        sx={{ height }}
    />
);

/** A priority tier, named rather than coded. */
export const TierPill: React.FC<{ code: string; showCode?: boolean }> = ({ code, showCode = true }) => {
    const meta = tierMeta(code);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 1,
                py: 0.3,
                borderRadius: '999px',
                fontSize: '0.6875rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                color: ink(meta.color),
                backgroundColor: `${meta.color}22`,
                border: `1px solid ${meta.color}55`
            }}
        >
            {showCode ? tierLabel(code) : meta.label}
        </Box>
    );
};

/**
 * One figure with its plain-language label.
 *
 * The technical name, when there is one, sits underneath as secondary text —
 * never in the heading, which is where the previous cards put it.
 */
export const StatBlock: React.FC<{
    label: string;
    value: string;
    definition?: string;
    /** e.g. "ROC AUC" — shown small, below the label. */
    technicalName?: string;
    detail?: string;
    accent?: string;
    /**
     * Too little evidence behind the figure to state it as a result. The value
     * is muted and labelled rather than hidden, so the reader can still see
     * what the run produced.
     */
    indicative?: boolean;
}> = ({ label, value, definition, technicalName, detail, accent, indicative = false }) => (
    <Box>
        <Box className="flex items-center gap-1.5">
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontWeight: 500 }}>{label}</Typography>
            {definition && <MetricInfo definition={definition} />}
        </Box>
        <Typography sx={{ fontSize: indicative ? '1.25rem' : '1.5rem', fontWeight: indicative ? 600 : 800, color: indicative ? dsVar('fg-muted') : accent ? ink(accent) : dsVar('fg-body'), lineHeight: 1.2, mt: 0.5 }}>
            {value}
        </Typography>
        {indicative && (
            <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mt: 0.25 }}>Indicative only</Typography>
        )}
        {technicalName && (
            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-faint'), mt: 0.25 }}>{technicalName}</Typography>
        )}
        {detail && (
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mt: 0.5 }}>{detail}</Typography>
        )}
    </Box>
);
